import * as THREE from 'three';

export interface BloodPool {
  spawn(position: THREE.Vector3, intensity?: number): void;
  update(deltaSeconds: number): void;
  dispose(): void;
}

interface Droplet {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
}

const BLOOD_STORAGE_KEY = 'smashparty_settings.graphics.blood-effects';

function bloodEnabled(): boolean {
  try {
    return window.localStorage.getItem(BLOOD_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function createBloodPool(parent: THREE.Group): BloodPool {
  const geometry = new THREE.SphereGeometry(0.035, 8, 6);
  const material = new THREE.MeshStandardMaterial({
    color: 0x8a0612,
    emissive: 0x260006,
    emissiveIntensity: 0.2,
    roughness: 0.75,
    metalness: 0,
    transparent: true,
    opacity: 0.92,
  });
  const active: Droplet[] = [];
  let seed = 0x51f15e;
  let disposed = false;

  function rand(): number {
    seed = Math.imul(seed ^ (seed >>> 15), 2246822507);
    seed = Math.imul(seed ^ (seed >>> 13), 3266489909);
    return ((seed ^= seed >>> 16) >>> 0) / 4294967296;
  }

  return {
    spawn(position: THREE.Vector3, intensity = 1): void {
      if (disposed || !bloodEnabled()) return;
      const count = Math.max(3, Math.min(14, Math.round(5 + intensity * 4)));
      for (let i = 0; i < count; i += 1) {
        const mesh = new THREE.Mesh(geometry, material.clone());
        const angle = rand() * Math.PI * 2;
        const speed = 0.45 + rand() * 0.85;
        mesh.position.copy(position);
        mesh.position.y += 0.25 + rand() * 0.2;
        mesh.scale.setScalar(0.75 + rand() * 1.1);
        parent.add(mesh);
        active.push({
          mesh,
          velocity: new THREE.Vector3(
            Math.cos(angle) * speed,
            0.35 + rand() * 0.85,
            Math.sin(angle) * speed,
          ),
          life: 0,
          maxLife: 0.45 + rand() * 0.35,
        });
      }
    },

    update(deltaSeconds: number): void {
      for (let i = active.length - 1; i >= 0; i -= 1) {
        const droplet = active[i]!;
        droplet.life += deltaSeconds;
        droplet.velocity.y -= 2.8 * deltaSeconds;
        droplet.mesh.position.addScaledVector(droplet.velocity, deltaSeconds);
        const t = Math.min(1, droplet.life / droplet.maxLife);
        droplet.mesh.scale.setScalar(Math.max(0.08, 1 - t * 0.65));
        if (droplet.mesh.material instanceof THREE.MeshStandardMaterial) {
          droplet.mesh.material.opacity = 0.92 * (1 - t);
        }
        if (t >= 1) {
          parent.remove(droplet.mesh);
          if (droplet.mesh.material instanceof THREE.Material) droplet.mesh.material.dispose();
          active.splice(i, 1);
        }
      }
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const droplet of active) {
        parent.remove(droplet.mesh);
        if (droplet.mesh.material instanceof THREE.Material) droplet.mesh.material.dispose();
      }
      active.length = 0;
      geometry.dispose();
      material.dispose();
    },
  };
}
